import { useState } from "react";
import { Phone, Mail, MessageCircle, Clock, Send } from "lucide-react";
import { toast } from "sonner";
import Seo from "@/components/Seo";
import { PageHeader, ConsultButton, FaqAccordion } from "@/components/sections";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { site } from "@/config/site";
import { openWhatsApp, telUrl, trackPhoneClick } from "@/lib/whatsapp";
import api, { formatApiErrorDetail } from "@/lib/api";
import { getAttribution } from "@/hooks/useAttribution";
import { trackEvent, EVENTS } from "@/lib/analytics";

const FAQS = [
  { q: "Can Bonsai assist clients across South Africa?", a: "Yes. We work with clients in various provinces and can support you remotely where appropriate." },
  { q: "What information should I prepare?", a: "A general overview of your business or personal situation is helpful — your entity type, industry, approximate turnover and the services you think you need. The assessment guides you through this." },
  { q: "Can I switch accountants?", a: "Yes. Many clients move to Bonsai Financial from a previous accountant or bookkeeper. We can discuss the process during consultation." },
  { q: "Can Bonsai assist if my accounting records are behind?", a: "Yes. We regularly help clients bring records up to date. Let us know the current status in your assessment." },
  { q: "Does Bonsai work with individuals as well as businesses?", a: "Yes. We assist individuals, sole proprietors, companies, partnerships and professional practices." },
  { q: "Can Bonsai assist professional practices and contractors?", a: "Yes. Professional practices and independent contractors are among the clients we are well positioned to support." },
];

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "", consent: false });
  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.consent) {
      toast.error("Please provide consent so we may contact you.");
      return;
    }
    const [first, ...rest] = form.name.trim().split(" ");
    setSending(true);
    try {
      const { data } = await api.post("/leads", {
        first_name: first || form.name,
        surname: rest.join(" ") || "-",
        phone: form.phone,
        email: form.email,
        additional_notes: form.message,
        services_required: ["Not Sure"],
        preferred_contact: "Email",
        consent: form.consent,
        attribution: { ...getAttribution(), source: getAttribution().source || "contact-form" },
      });
      trackEvent(EVENTS.LEAD, { ref: data.lead_ref, from: "contact" });
      toast.success("Thank you — your enquiry has been received.");
      setForm({ name: "", email: "", phone: "", message: "", consent: false });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not send. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <Seo
        title="Contact Bonsai Financial"
        description="Contact Bonsai Financial by phone, WhatsApp or email, or request a consultation. Professional accounting and tax services in South Africa."
        path="/contact"
      />
      <PageHeader
        eyebrow="Contact"
        title="Let's talk about your financial support."
        intro="Request a consultation, send a quick message, or reach us directly on WhatsApp or by phone."
      />

      <section className="py-16 sm:py-20">
        <div className="mx-auto grid max-w-7xl gap-10 px-4 sm:px-6 lg:grid-cols-2 lg:px-8">
          {/* Contact methods */}
          <div>
            <div className="rounded-2xl bg-[#133827] p-8 text-white">
              <h2 className="font-display text-xl font-bold">Reach Bonsai Financial</h2>
              <div className="mt-6 space-y-5">
                <a href={telUrl()} onClick={trackPhoneClick} data-testid="contact-phone-link" className="flex items-center gap-3 hover:text-[#C68B59]">
                  <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-white/10"><Phone className="h-5 w-5" /></span>
                  <span><span className="block text-xs text-slate-400">Phone</span>{site.phoneDisplay}</span>
                </a>
                <button onClick={() => openWhatsApp("general")} data-testid="contact-whatsapp-link" className="flex w-full items-center gap-3 text-left hover:text-[#C68B59]">
                  <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-white/10"><MessageCircle className="h-5 w-5 text-[#25D366]" /></span>
                  <span><span className="block text-xs text-slate-400">WhatsApp</span>Chat with us</span>
                </button>
                <a href={`mailto:${site.email}`} data-testid="contact-email-link" className="flex items-center gap-3 hover:text-[#C68B59]">
                  <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-white/10"><Mail className="h-5 w-5" /></span>
                  <span><span className="block text-xs text-slate-400">Email</span>{site.email}</span>
                </a>
                <div className="flex items-center gap-3">
                  <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-white/10"><Clock className="h-5 w-5" /></span>
                  <span><span className="block text-xs text-slate-400">Business Hours</span>{site.businessHours}</span>
                </div>
              </div>
              <div className="mt-8 rounded-xl bg-white/5 p-4">
                <p className="text-sm text-slate-300">Looking for a structured start?</p>
                <ConsultButton from="contact-page" testId="contact-consult-btn" className="mt-3 w-full bg-[#C68B59] hover:bg-[#b2794a]" />
              </div>
            </div>
          </div>

          {/* Quick message form */}
          <div>
            <h2 className="font-display text-xl font-bold text-[#18201B]">Send a quick message</h2>
            <p className="mt-2 text-sm text-[#526057]">For a detailed enquiry, the consultation assessment captures everything we need.</p>
            <form onSubmit={submit} className="mt-6 space-y-4" data-testid="contact-form">
              <div>
                <Label htmlFor="c-name">Name</Label>
                <Input id="c-name" required data-testid="contact-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5" />
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <Label htmlFor="c-email">Email</Label>
                  <Input id="c-email" type="email" required data-testid="contact-email-input" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1.5" />
                </div>
                <div>
                  <Label htmlFor="c-phone">Mobile</Label>
                  <Input id="c-phone" required data-testid="contact-phone-input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1.5" />
                </div>
              </div>
              <div>
                <Label htmlFor="c-msg">Message</Label>
                <Textarea id="c-msg" rows={4} data-testid="contact-message-input" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="mt-1.5" />
              </div>
              <div className="flex items-start gap-3">
                <Checkbox id="c-consent" checked={form.consent} data-testid="contact-consent-checkbox" onCheckedChange={(v) => setForm({ ...form, consent: !!v })} className="mt-1" />
                <Label htmlFor="c-consent" className="text-sm font-normal leading-snug text-[#526057]">
                  I consent to Bonsai Financial contacting me regarding my enquiry. I understand that submitting this form does not by itself create a professional engagement.
                </Label>
              </div>
              <Button type="submit" disabled={sending} data-testid="contact-submit-btn" className="w-full bg-[#133827] text-white hover:bg-[#0d261a]">
                {sending ? "Sending…" : (<>Send Message <Send className="ml-1.5 h-4 w-4" /></>)}
              </Button>
            </form>
          </div>
        </div>
      </section>

      <section className="bg-white py-16 sm:py-20">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
          <h2 className="text-center font-display text-2xl font-bold text-[#18201B] sm:text-3xl">Frequently asked questions</h2>
          <FaqAccordion items={FAQS} />
        </div>
      </section>
    </>
  );
}
